import { gql, useQuery } from "@apollo/client";
import { AppContext } from "App";
import { Tag } from "components/base/base";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Image from "components/base/Image";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import queryString from "query-string";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import http from "utils/http";
import { searchByProp } from "utils/search";
import ProductBarcode from "./ProductBarcode";
import productProps from "./productProps";
import ProductTable from "./ProductTable";

const FETCH_DATA_FOR_PRODUCT_PAGE = gql`
  query FETCH_DATA_FOR_PRODUCT_PAGE {
    productLines(prefetchForProductsPage: true, hasActiveProducts: true) {
      results {
        id
        name
        productsSearchString
        age
        hsCode
        hsCodeForEu
        hsCodeForUs
        pantone
        mainImage(size: "300x300")
        materials {
          id
          material {
            id
            name
          }
        }
        warnings {
          id
          name
          content
        }
        labTestStandards {
          id
          name
        }
      }
    }
  }
`;

function Products() {
  const { loading, error, data } = useQuery(FETCH_DATA_FOR_PRODUCT_PAGE);
  const location = useLocation();
  const navigate = useNavigate();

  const {
    settings: { metric },
  } = useContext(AppContext);

  const productLinesContainerRef = useRef();
  const [scrollingProductLinesContainer, setScrollingProductLinesContainer] =
    useState(false);

  const [searchText, setSearchText] = useState("");
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  const q = queryString.parse(location.search);
  const initialLines = q.lines ? q.lines.split(",") : [];
  const [lines, setLines] = useState(initialLines);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const barcodeModal = useModals();

  useEffect(() => {
    const searchParams = new URLSearchParams();
    if (lines.length > 0) searchParams.append("lines", lines.join(","));
    const search = searchParams.toString();
    navigate({ search });
  }, [lines]);

  function previewBarcode(product) {
    barcodeModal.present({
      title: "Generate Barcode",
      center: true,
      children: <ProductBarcode product={product} />,
    });
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const hasSelectedProducts =
    selectedProducts.length > 0 || selectedFields.length > 0;

  function selectProducts(products) {
    if (products.length === 1) {
      const product = products[0];
      if (selectedProducts.find((p) => p.id === product.id)) {
        setSelectedProducts(
          selectedProducts.filter((p) => p.id !== product.id),
        );
      } else {
        setSelectedProducts([...selectedProducts, product]);
      }
    } else {
      if (selectedProducts.length > 0) {
        setSelectedProducts([]);
      } else {
        setSelectedProducts(products);
      }
    }
  }

  function selectField(field) {
    if (selectedFields.find((f) => f.field === field.field)) {
      setSelectedFields(selectedFields.filter((f) => f.field !== field.field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  }

  function exportPDF() {
    const products = selectedProducts;
    console.log("products", products);
    const fields = selectedFields.length > 0 ? selectedFields : productProps;
    const url = `/export/product/pdf/`;
    let data = [];
    let i,
      j,
      chunk = 3;
    for (i = 0, j = products.length; i < j; i += chunk) {
      const productsForThisTable = products.slice(i, i + chunk);
      data.push({
        headerRow: ["Products", ...productsForThisTable.map((i) => i.name)],
        bodyRows: fields.map((f) => {
          let property = productProps.find((p) => p.field === f.field);
          return [
            property.displayName,
            ...productsForThisTable.map((product) => {
              switch (property.field) {
                case "name":
                  return product.name;
                case "images":
                  return product.id;
                default:
                  return property.value
                    ? property.value(product, metric)
                    : product[property.field];
              }
            }),
          ];
        }),
      });
    }
    setIsExportingPDF(true);
    http
      .post(
        url,
        { data, fileType: "pdf" },
        {
          baseURL: process.env.REACT_APP_SERVER_BASE_URL,
          timeout: 60000,
          responseType: "blob",
          withCredentials: true,
        },
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Waboba-Products-Data.pdf");
        document.body.appendChild(link);
        link.click();
        setIsExportingPDF(false);
      })
      .catch((error) => {
        alert(`Oops! ${error}`);
        setIsExportingPDF(false);
      });
  }

  function exportExcel() {
    const products = selectedProducts;
    const fields = selectedFields.length > 0 ? selectedFields : productProps;
    const data = {
      headerRow: ["Products", ...products.map((i) => i.name)],
      bodyRows: fields.map((f) => {
        let property = productProps.find((p) => p.field === f.field);
        return [
          property.displayName,
          ...products.map((product) => {
            switch (property.field) {
              case "name":
                return product.name;
              case "images":
                return product.images.map((i) => i.url);
              default:
                return property.value
                  ? property.value(product, metric)
                  : product[property.field];
            }
          }),
        ];
      }),
    };
    const url = `/export/product/xlsx/`;
    setIsExportingExcel(true);
    http
      .post(
        url,
        { data, fileType: "excel" },
        {
          baseURL: process.env.REACT_APP_SERVER_BASE_URL,
          timeout: 60000,
          responseType: "blob",
          withCredentials: true,
        },
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Waboba-Products-Data.xlsx");
        document.body.appendChild(link);
        link.click();
        setIsExportingExcel(false);
      })
      .catch((error) => {
        alert(`Oops! ${error}`);
        setIsExportingExcel(false);
      });
  }

  const productLines = data.productLines.results
    .filter((i) => (showOnlySelected ? lines.includes(i.id) : true))
    .filter(
      (i) =>
        lines.includes(i.id) ||
        searchByProp(i, ["name", "productsSearchString"], searchText),
    )
    .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

  return (
    <div className="flex flex-col flex-1 h-full">
      <header className="mb-4">
        <div className="flex items-center justify-between my-2">
          <div className="w-full mt-2 lg:w-1/2 mx-4 hidden lg:block">
            <SearchBar
              autoFocus
              className="py-1"
              placeholder="Search Product Name, Product Line Number, Product groups"
              onChange={setSearchText}
            />
          </div>
          <div className="flex flex-end mx-4 mt-2 lg:mt-0 space-x-4">
            {lines.length > 0 ? (
              <Button
                onClick={() => {
                  setLines([]);
                  setShowOnlySelected(false);
                }}
              >
                Clear Selection
              </Button>
            ) : null}

            <Button onClick={() => setLines(productLines.map((i) => i.id))}>
              Select All
            </Button>

            {lines.length > 0 && (
              <Button
                onClick={() => {
                  setShowOnlySelected((prev) => !prev);
                }}
              >
                {showOnlySelected ? "Show All" : "Show Selected"}
              </Button>
            )}

            {hasSelectedProducts && (
              <>
                <Button loading={isExportingPDF} onClick={exportPDF}>
                  Export PDF
                </Button>
                <Button loading={isExportingExcel} onClick={exportExcel}>
                  Export Excel
                </Button>
              </>
            )}
          </div>
        </div>

        <div
          ref={productLinesContainerRef}
          className="flex overflow-auto scrolling-touch animate-fadeInDown opacity-0"
          onScroll={() => {
            setScrollingProductLinesContainer(true);
          }}
          onMouseLeave={() => setScrollingProductLinesContainer(false)}
        >
          <div
            className={`flex lg:flex-wrap animate-ease-5
               ${productLines.length < 20 || searchText.trim() !== "" ? "" : "lg:h-24"}`}
            style={{
              height: scrollingProductLinesContainer
                ? productLinesContainerRef.current.scrollHeight
                : null,
            }}
          >
            {productLines.map((i, index) => {
              const selected = lines.includes(i.id);
              return (
                <div
                  className={`ml-4 mt-2
                     ${productLines.length === index + 1 ? "mr-4" : ""}`}
                  key={i.id}
                >
                  <Tag
                    className="text-xs"
                    selected={selected}
                    onClick={(_) => {
                      let newLines = [];
                      if (selected) {
                        newLines = lines.filter((id) => id !== i.id);
                        setSelectedProducts([]);
                        setSelectedFields([]);
                      } else {
                        newLines = [...lines, i.id];
                      }
                      setLines(newLines);
                      if (newLines.length === 0) {
                        setShowOnlySelected(false);
                      }
                    }}
                    title={i.name}
                    leftView={
                      i.mainImage ? (
                        <Image
                          className="mr-2"
                          size="w-4 h-4"
                          src={i.mainImage}
                        />
                      ) : null
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      </header>

      <section className="flex flex-1 relative overflow-auto text-sm border-t border-gray-200 dark:border-gray-700">
        {lines.length > 0 ? (
          <ProductTable
            selectedProducts={selectedProducts}
            selectedFields={selectedFields}
            selectProducts={selectProducts}
            selectField={selectField}
            productLines={lines}
            previewBarcode={previewBarcode}
          />
        ) : null}
      </section>
    </div>
  );
}

export default Products;
